import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { api } from './api';
import { MIN_PASSWORD, PASSWORD_HINT } from './passwordPolicy';
import { useAuth } from './AuthContext';
import { homeForRole } from './externalRoles';
import { Input } from '../components/ui/input';
import PasswordInput from './shared/PasswordInput';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { BRAND } from './kit/tokens';
import { Loader2, Home, ArrowRight } from 'lucide-react';

// ticket 115: lands here from the link-claim email sent when someone signs
// up with a password on an ADR-0006 link (borrower) row's email. The
// password is collected HERE, from whoever actually clicked the link — not
// carried over from the signup request that triggered the email — so
// whoever merely typed the email address never gets to choose the
// credential that lands on the account.
// Ticket 166: every password and referral signup lands here too — the
// account (and its workspace) is created by this page's POST.
const detailOf = (e) => e.response?.data?.detail;
const messageOf = (e) =>
  (typeof detailOf(e) === 'object' ? detailOf(e)?.message : detailOf(e)) || 'This link is not valid.';

function Shell({ children }) {
  return (
    <div className="min-h-screen flex" style={{ background: '#faf7f2' }}>
      <div className="hidden lg:flex lg:w-5/12 relative overflow-hidden" style={{ background: `linear-gradient(160deg, ${BRAND.navyDeep} 0%, ${BRAND.navy} 60%, ${BRAND.navyLight} 100%)` }}>
        <div className="absolute inset-0 opacity-40" style={{ backgroundImage: 'radial-gradient(circle at 30% 20%, rgba(141,198,63,0.35) 0%, transparent 45%)' }} />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white"><Home size={14} /> BrokerForYou</Link>
          <div>
            <img src="/assets/tcl-logo-white.png" alt="Top Choice Lending" className="h-14" />
            <h1 className="font-serif text-5xl font-semibold mt-8">Almost <span className="text-brand-accent-soft">there</span>.</h1>
          </div>
          <div className="text-xs text-white/40">Secure verification link</div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md" data-testid="verify-email-page">{children}</div>
      </div>
    </div>
  );
}

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const { completeGoogleLogin } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (!token) {
    return (
      <Shell>
        <h2 className="font-serif text-3xl font-semibold text-brand-navy">Link unavailable</h2>
        <p className="text-slate-500 mt-2">This link is missing its token.</p>
        <Button variant="accent" className="mt-6 justify-center" onClick={() => navigate('/portal/login')}>
          Go to sign in <ArrowRight size={14} />
        </Button>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell>
        <h2 className="font-serif text-3xl font-semibold text-brand-navy">Link unavailable</h2>
        <p className="text-slate-500 mt-2">{error}</p>
        <Button variant="accent" className="mt-6 justify-center" onClick={() => navigate('/portal/login')}>
          Go to sign in <ArrowRight size={14} />
        </Button>
      </Shell>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/auth/verify-link/${encodeURIComponent(token)}`, { name, password });
      if (data.mfa_required) {
        navigate('/portal/login', { replace: true, state: { mfa: { mfaToken: data.mfa_token, enrolled: data.enrolled } } });
        return;
      }
      const u = completeGoogleLogin({ access_token: data.access_token, user: data.user });
      const dest = homeForRole(u.role);
      navigate(dest, { replace: true });
    } catch (e) {
      setError(
        e.response?.status === 410 ? 'This verification link has expired or was already used.'
          : e.response?.status === 404 ? 'This verification link is not valid.'
            : e.response?.status === 409 ? 'This account has already been set up a different way. Try signing in.'
              : messageOf(e)
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell>
      <h2 className="font-serif text-3xl font-semibold text-brand-navy">Finish setting up your account</h2>
      <p className="text-slate-500 mt-2">Choose a password to activate your account.</p>
      <form onSubmit={submit} className="space-y-4 mt-8">
        <div>
          <Label>Full name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5" data-testid="verify-name" />
        </div>
        <div>
          <Label>Password</Label>
          <PasswordInput
            value={password} onChange={(e) => setPassword(e.target.value)}
            required minLength={MIN_PASSWORD} containerClassName="mt-1.5" placeholder={PASSWORD_HINT} data-testid="verify-password"
          />
        </div>
        <Button type="submit" disabled={busy} variant="accent" className="w-full justify-center" data-testid="verify-submit">
          {busy ? <><Loader2 size={16} className="animate-spin" /> Activating...</> : <>Activate account <ArrowRight size={14} /></>}
        </Button>
      </form>
    </Shell>
  );
}
